// Phase 2 — FileDropzone. Drag/drop + preview UI on top of the EXISTING AWS
// uploader (src/Components/SubComponents/awsUpload.ts). The upload contract is
// unchanged: uploadPhotoToAWS({ file }, cb, type) -> cb(fileObjWithFileUuid).
// Only the trigger UI is new. See docs/04 §4.2 and docs/09 Phase 2.
import * as React from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { uploadPhotoToAWS } from "../Components/SubComponents/awsUpload";
import { cn } from "lib/utils";

export interface UploadedFile {
  fileUuid: string;
  name: string;
  previewUrl: string;
  raw: any;
}

/** Same signature as uploadPhotoToAWS; overridable for tests/demos. */
export type Uploader = (fileObj: { file: File }, cb: (res: any) => void, type: number) => void;

export interface FileDropzoneProps {
  /** AWS "type" location code passed straight through (1=product img … 5=banner). */
  type: number;
  onUploaded: (file: UploadedFile) => void;
  accept?: string;
  disabled?: boolean;
  label?: string;
  hint?: string;
  /** Defaults to the real AWS uploader; inject a stub in demos/tests. */
  uploader?: Uploader;
  className?: string;
}

export function FileDropzone({
  type,
  onUploaded,
  accept = "image/*",
  disabled = false,
  label = "Click to upload or drag and drop",
  hint,
  uploader = uploadPhotoToAWS,
  className,
}: FileDropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [preview, setPreview] = React.useState<UploadedFile | null>(null);

  const handleFile = (file: File) => {
    if (!file || disabled) return;
    const previewUrl = URL.createObjectURL(file);
    setUploading(true);
    // Preserve the legacy call shape exactly.
    uploader({ file }, (res: any) => {
      setUploading(false);
      const uploaded: UploadedFile = {
        fileUuid: res?.fileUuid,
        name: file.name,
        previewUrl,
        raw: res,
      };
      setPreview(uploaded);
      onUploaded(uploaded);
    }, type);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview.previewUrl);
    setPreview(null);
  };

  return (
    <div className={cn("mz", className)}>
      {preview ? (
        <div className="relative flex items-center gap-3 rounded-xl border p-3">
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img src={preview.previewUrl} alt={preview.name} className="h-12 w-12 rounded-lg object-cover" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{preview.name}</p>
            <p className="truncate text-xs text-muted-foreground">Uploaded</p>
          </div>
          <button
            type="button"
            onClick={clear}
            aria-label="Remove file"
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 text-center transition-colors outline-none",
            "hover:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring",
            dragging ? "border-primary bg-primary/5" : "border-border",
            (disabled || uploading) && "pointer-events-none opacity-60"
          )}
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          ) : (
            <UploadCloud className="h-6 w-6 text-muted-foreground" />
          )}
          <span className="text-sm font-medium text-foreground">{uploading ? "Uploading…" : label}</span>
          {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        </button>
      )}
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onInputChange} />
    </div>
  );
}
